import { useState } from "react";
import { Certificate } from "@/services/backend/vbs/certificates/types/certificate";
import {
  UpdateIssueDateRequest,
  UpdateSegmentsRequest,
  UpdateWorkEndedAtRequest,
  UpdateWorkStartedAtRequest,
  useVbsCertificateShowQuery,
  useVbsCertificateUpdateIssueDateMutation,
  useVbsCertificateUpdatePostalAddressMutation,
  useVbsCertificateUpdateSegmentsMutation,
  useVbsCertificateUpdateServiceAddressMutation,
  useVbsCertificateUpdateWorkEndedAtMutation,
  useVbsCertificateUpdateWorkStartedAtMutation,
} from "@/services/backend/vbs/certificates/api/certificate-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import t from "@/lang/lang";
import {
  SegmentKind,
  SegmentKindList,
} from "@/services/backend/vbs/treatments/types/segmentKind";
import { ServiceAddressCardWithMutation } from "@/services/backend/addresses/components/service-address-card";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { InputErrorMessages } from "@/shared/components/errors/input-error-messages";
import {
  errorsFor,
  fieldErrorsForPrefix,
  useErrDocumentNotReady,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { fieldErrors } from "@/shared/lib/errors/validation-error";
import { PostalAddressCardWithMutation } from "@/services/backend/addresses/components/postal-address-card";
import { DateInput } from "@/shared/components/form/date-input";

export function CertificateComponent(props: {
  certificateId: string;
  disabled?: boolean;
}) {
  const { certificateId, disabled = false } = props;
  const { data, isLoading, error } = useVbsCertificateShowQuery({
    id: certificateId,
  });
  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();

  if (isLoading) {
    return <CertificateSkeleton />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="space-y-2">
      <CertificateEditForm certificate={data!} disabled={disabled} />
      <PostalAddressCardWithMutation
        id={data!.id}
        address={data!.postalAddress}
        mutation={useVbsCertificateUpdatePostalAddressMutation}
        fieldErrors={fieldErrorsForPrefix(notReadyError, "postalAddress.")}
        resetFieldError={(field) =>
          resetNotReadyErrorField(`postalAddress.${field}`)
        }
        disabled={disabled}
      />
      <ServiceAddressCardWithMutation
        id={data!.id}
        address={data!.serviceAddress}
        mutation={useVbsCertificateUpdateServiceAddressMutation}
        fieldErrors={fieldErrorsForPrefix(notReadyError, "serviceAddress.")}
        resetFieldError={(field) =>
          resetNotReadyErrorField(`serviceAddress.${field}`)
        }
        disabled={disabled}
      />
    </div>
  );
}

function CertificateSkeleton() {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("Zertifikat Daten")}</CardTitle>
          <CardDescription>{t("Basisdaten des Zertifikats")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

function CertificateEditForm(props: {
  certificate: Certificate;
  disabled?: boolean;
}) {
  const { certificate, disabled = false } = props;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Zertifikat Daten")}</CardTitle>
        <CardDescription>{t("Basisdaten des Zertifikats")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <IssueDateInput
          certificateId={certificate.id}
          issueDate={certificate.issueDate}
          disabled={disabled}
        />
        <WorkStartDateInput
          certificateId={certificate.id}
          workStartedAt={certificate.workStartedAt}
          disabled={disabled}
        />
        <WorkEndDateInput
          certificateId={certificate.id}
          workEndedAt={certificate.workEndedAt}
          disabled={disabled}
        />
        <SegmentSelectionComponent
          certificate={certificate}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}

function IssueDateInput(props: {
  certificateId: string;
  issueDate: string;
  disabled?: boolean;
}) {
  const { certificateId, issueDate, disabled = false } = props;
  const [request, setRequest] = useState<UpdateIssueDateRequest>({
    id: certificateId,
    issueDate,
  });
  const mutation = useVbsCertificateUpdateIssueDateMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: certificateId,
    issueDate,
  });
  const { notReadyError } = useErrDocumentNotReady();

  const errorMessages: string[] = [
    ...(errorsFor(notReadyError, "issueDate") ?? []),
    ...(fieldErrors(error, "issueDate") ?? []),
  ];

  return (
    <DateInput
      label={t("Datum der Ausstellung")}
      date={request.issueDate}
      onDateChange={(date) => setRequest({ ...request, issueDate: date ?? "" })}
      errors={errorMessages}
      disabled={disabled}
    />
  );
}

function WorkStartDateInput(props: {
  certificateId: string;
  workStartedAt: string;
  disabled?: boolean;
}) {
  const { certificateId, workStartedAt, disabled = false } = props;
  const [request, setRequest] = useState<UpdateWorkStartedAtRequest>({
    id: certificateId,
    workStartedAt,
  });
  const mutation = useVbsCertificateUpdateWorkStartedAtMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: certificateId,
    workStartedAt,
  });

  const { notReadyError } = useErrDocumentNotReady();

  const errorMessages: string[] = [
    ...(errorsFor(notReadyError, "workStartedAt") ?? []),
    ...(fieldErrors(error, "workStartedAt") ?? []),
  ];

  return (
    <DateInput
      label={t("Begin der Arbeiten")}
      date={request.workStartedAt}
      onDateChange={(date) =>
        setRequest({ ...request, workStartedAt: date ?? "" })
      }
      errors={errorMessages}
      disabled={disabled}
    />
  );
}

function WorkEndDateInput(props: {
  certificateId: string;
  workEndedAt: string;
  disabled?: boolean;
}) {
  const { certificateId, workEndedAt, disabled = false } = props;
  const [request, setRequest] = useState<UpdateWorkEndedAtRequest>({
    id: certificateId,
    workEndedAt,
  });
  const mutation = useVbsCertificateUpdateWorkEndedAtMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: certificateId,
    workEndedAt,
  });

  const { notReadyError } = useErrDocumentNotReady();

  const errorMessages: string[] = [
    ...(errorsFor(notReadyError, "workEndedAt") ?? []),
    ...(fieldErrors(error, "workEndedAt") ?? []),
  ];

  return (
    <DateInput
      label={t("Ende der Arbeiten")}
      date={request.workEndedAt}
      onDateChange={(date) =>
        setRequest({ ...request, workEndedAt: date ?? "" })
      }
      errors={errorMessages}
      disabled={disabled}
    />
  );
}

function SegmentSelectionComponent(props: {
  certificate: Certificate;
  disabled?: boolean;
}) {
  const { certificate, disabled = false } = props;
  const [request, setRequest] = useState<UpdateSegmentsRequest>({
    id: certificate.id,
    segments: certificate.segments,
  });
  const mutation = useVbsCertificateUpdateSegmentsMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: certificate.id,
    segments: certificate.segments,
  });

  const { notReadyError } = useErrDocumentNotReady();

  const segmentSelected = (segmentKind: SegmentKind): boolean =>
    request.segments.includes(segmentKind);

  const updateSelection = (
    segmentKind: SegmentKind,
    selection: boolean,
  ): void => {
    if (selection) {
      if (request.segments.includes(segmentKind)) {
        return;
      }
      setRequest({
        id: request.id,
        segments: [...request.segments, segmentKind],
      });
    } else {
      setRequest({
        id: request.id,
        segments: request.segments.filter((segment) => segment !== segmentKind),
      });
    }
  };

  const errorMessages: string[] = [
    ...(errorsFor(notReadyError, "segments") ?? []),
    ...(fieldErrors(error, "segments") ?? []),
  ];

  return (
    <div className="grid gap-1.5">
      <Label>{t("Behandelte Bauteilgruppen")}</Label>
      <div className="space-y-2">
        {SegmentKindList.map((segmentKind) => (
          <div className="flex items-center space-x-2" key={segmentKind}>
            <Switch
              checked={segmentSelected(segmentKind)}
              onCheckedChange={(newValue) =>
                updateSelection(segmentKind, newValue)
              }
              disabled={disabled}
            >
              <span className="sr-only">Enable notifications</span>
            </Switch>
            <Label>{t(segmentKind)}</Label>
          </div>
        ))}
      </div>
      <InputErrorMessages errors={errorMessages} />
    </div>
  );
}
